import React, { useEffect, useState, useMemo } from "react";
import { apiList, apiUpload, apiDelete, apiGetMedia } from "../../../services/Admin/media";
import { Grid, Box, Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import MsgError from "../../MsgError";
import MsgSuccess from "../../MsgSuccess";
import TableC from "../TableC";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { BsCheckCircleFill } from "react-icons/bs";
import moment from "moment";
import "moment/locale/es";
import AddMediaModal from "./AddMediaModal";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { getMimeTypeByFormat, fileTypes } from "./mediaUtils";
import IconButton from "@mui/material/IconButton";
import useAuth from "../../../hooks/useAuth";
import Card from "../../../components/Admin/Card";

export default function Management(props) {
    const { canBeSelected } = props;
    const { auth } = useAuth()

    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msgError, setMsgError] = useState([]);
    const [msgSuccess, setMsgSuccess] = useState([]);
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [loadingRemove, setLoadingRemove] = useState(false);
    const [fileRemove, setFileRemove] = useState({ url: null, id: null, key: null, type: null });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newFile, setNewFile] = useState(null);
    const [newText, setNewText] = useState("");
    const [newType, setNewType] = useState("");
    const [user, setUser] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");

    const columns = useMemo(() => [
        { Header: "Persona", accessor: "user_name" },
        { Header: "Tipo fichero", accessor: "type" },
        { Header: "Descripción", accessor: "text" },
        { Header: "Fecha de carga", accessor: "created_at" },
        { Header: "Acciones", accessor: "actions" }
    ], []);

    useEffect(() => {
        getMediaData();
    }, []);

    const getMediaData = () => {
        setLoading(true);
        apiList({ limit: 50, offset: 0 })
            .then(resp => {
                setMedia(mapMedia(resp.data));
                setLoading(false);
            }).catch(err => {
                setMsgError(err.response?.data?.msg);
                setLoading(false);
            });
    };

    const handleMenuOpen = (event, file) => {
        setAnchorEl(event.currentTarget);
        setSelectedFile(file);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedFile(null);
    };

    const handleDownload = async () => {
        if (selectedFile?.path) {
            try {
                const resp = await apiGetMedia(selectedFile.path);
                const base64Content = resp.data?.content;
                const format = resp.data?.format || selectedFile.format;
                if (base64Content) {
                    const filename = selectedFile.text || selectedFile.url || `archivo.${format || "pdf"}`;
                    const mimeType = getMimeTypeByFormat(format);
                    const link = document.createElement("a");
                    link.href = `data:${mimeType};base64,${base64Content}`;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (err) {
                setMsgError("Error al descargar el archivo.");
            }
        }
        handleMenuClose();
    };

    const handleDownloadFile = async (file) => {
        if (file?.path) {
            try {
                const resp = await apiGetMedia(file.path);
                const base64Content = resp.data?.content;
                const format = resp.data?.format || file.format;
                if (base64Content) {
                    const filename = file.text || file.url || `archivo.${format || "pdf"}`;
                    const mimeType = getMimeTypeByFormat(format);
                    const link = document.createElement("a");
                    link.href = `data:${mimeType};base64,${base64Content}`;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (err) {
                setMsgError("Error al descargar el archivo.");
            }
        }
    }

    const handleDeleteClick = () => {
        setFileRemove({ url: selectedFile.url, id: selectedFile.id, key: selectedFile.key, type: selectedFile.type });
        setConfirmOpen(true);
        handleMenuClose();
    };

    const handleConfirmClose = () => {
        setConfirmOpen(false);
        setFileRemove({ url: null, id: null, key: null, type: null });
    };

    const handleConfirmDelete = () => {
        setLoadingRemove(true);
        setMsgError([]);
        apiDelete(fileRemove.id).then(resp => {
            setMsgSuccess(resp.data?.msg);
            getMediaData();
            setFileRemove({ url: null, id: null, key: null, type: null });
            setConfirmOpen(false);
            setLoadingRemove(false);
        }).catch(err => {
            setMsgError(err.response.data?.msg);
            setConfirmOpen(false);
            setLoadingRemove(false);
        });
    };

    const mapMedia = (data) => {
        return data.map((file, key) => ({
            ...file,
            type: fileTypes.find(ft => ft.value === file.type)?.label || file.type,
            created_at: moment.unix(file.created_at).locale('es').format('D MMM, YYYY'),
            actions: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {auth?.primaryUser?.role === "ROLE_ADMIN" ? (
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, { ...file, key, relative_path: file.relative_path, format: file.format })}>
                            <MoreHorizIcon />
                        </IconButton>
                    ) : (
                        <Button variant="outlined" size="small" onClick={() => handleDownloadFile(file)}>Descargar</Button>
                    )}
                    {file.select && (
                        <BsCheckCircleFill style={{ color: '#1976d2', marginLeft: '8px', fontSize: '20px' }} />
                    )}
                </div>
            )
        }));
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
        setNewFile(null);
        setNewText("");
        setNewType("");
    };

    const handleAddMedia = (e) => {
        e.preventDefault();
        setMsgError([]);
        setMsgSuccess([]);
        setLoadingAdd(true);

        let f = new FormData();
        if (newFile) f.append("file", newFile);
        f.append("path", "documents/");
        f.append("text", newText);
        f.append("type", newType);
        f.append("user_id", user || "");

        apiUpload(f).then(resp => {
            setMsgSuccess(resp.data?.msg);
            getMediaData();
            setLoadingAdd(false);
            handleCloseAddModal();
        }).catch(err => {
            setMsgError(err.response?.data?.msg || "Error al guardar.");
            setLoadingAdd(false);
        });
    };

    return (
        <Card>
            <div className="cointainer cointainer-bt">
                <h1>Documentación</h1>
                {!loading && auth?.primaryUser?.role === "ROLE_ADMIN" && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setOpenAddModal(true)}
                            style={{ width: 200 }}
                        >
                            + Añadir
                        </Button>
                    </div>
                )}
            </div>
            {!loading ? (
                <TableC
                    columns={columns}
                    data={media}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                />
            ) : (
                <Box sx={{ maxWidth: "50px", marginLeft: "auto", marginRight: "auto", marginTop: "100px", marginBottom: "100px" }}>
                    <CircularProgress />
                </Box>
            )}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleDownload}>Descargar</MenuItem>
                {!canBeSelected && auth?.primaryUser?.role === "ROLE_ADMIN" && (
                    <MenuItem onClick={handleDeleteClick}>Eliminar</MenuItem>
                )}
            </Menu>
            <ConfirmDeleteDialog
                open={confirmOpen}
                onClose={handleConfirmClose}
                onConfirm={handleConfirmDelete}
                fileRemove={fileRemove}
                loadingRemove={loadingRemove}
            />
            <AddMediaModal
                open={openAddModal}
                onClose={() => setOpenAddModal(false)}
                onAdd={handleAddMedia}
                loadingAdd={loadingAdd}
                msgError={msgError}
                msgSuccess={msgSuccess}
                newFile={newFile}
                setNewFile={setNewFile}
                newText={newText}
                setNewText={setNewText}
                newType={newType}
                setNewType={setNewType}
                fileTypes={fileTypes}
                user={user}
                setUser={setUser}
            />
        </Card>
    );
}
